/**
 * One sound at a time (Gordon 2026-10-31: "Playing videos and tracks are
 * still colliding. One should turn off the other. And vice versa.").
 *
 * Whatever starts playing WITH SOUND claims focus and everything else that is
 * playing with sound pauses:
 *  - the BB player (its <audio>, or its YouTube iframe for YouTube tracks —
 *    marked `data-bb-player-yt`) is one source: "bb-player";
 *  - any other <video>/<audio> on the page (captured `play`/`volumechange`);
 *  - any YouTube embed, through YouTube's iframe postMessage protocol. Embeds
 *    without `enablejsapi=1` get it added (one reload as they appear), then a
 *    `listening` handshake makes them report playerState / muted.
 * Muted media never claims and is never paused — background loops (the Home
 * hero, slideshow clips, the sign-in video) keep running silently.
 *
 * The BB player's <audio> is a `new Audio()` that is never in the document, so
 * its events never reach the document listeners — it gets its own (Gordon
 * 2026-11-02: a video stopped the track, but starting a track left the video
 * playing).
 */

export type FocusSource = "bb-player" | Element;
type Listener = (source: FocusSource) => void;

const listeners = new Set<Listener>();
const ytState = new WeakMap<HTMLIFrameElement, { playing: boolean; muted: boolean }>();
let installed = false;
let getPlayerAudio: () => HTMLMediaElement | null = () => null;
const wiredPlayers = new WeakSet<HTMLMediaElement>();

const YT_ORIGINS = ["https://www.youtube.com", "https://www.youtube-nocookie.com"];
const isYtFrame = (el: Element): el is HTMLIFrameElement =>
  el.tagName === "IFRAME" && /youtube(-nocookie)?\.com\/embed\//.test((el as HTMLIFrameElement).src || "");

function ytCommand(frame: HTMLIFrameElement, func: string) {
  try { frame.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: "" }), "*"); } catch { /* cross-origin hiccup */ }
}

/** Pause everything playing with sound except the claimant. */
function claim(source: FocusSource) {
  const playerAudio = getPlayerAudio();
  document.querySelectorAll("video, audio").forEach((node) => {
    const el = node as HTMLMediaElement;
    if (el === source || el === playerAudio) return;
    if (!el.paused && !el.muted && el.volume > 0) { try { el.pause(); } catch { /* ignore */ } }
  });
  document.querySelectorAll("iframe").forEach((node) => {
    const frame = node as HTMLIFrameElement;
    if (frame === source || !isYtFrame(frame)) return;
    if (source === "bb-player" && frame.hasAttribute("data-bb-player-yt")) return;
    const st = ytState.get(frame);
    if (st?.playing && !st.muted) { ytCommand(frame, "pauseVideo"); st.playing = false; }
  });
  listeners.forEach((fn) => { try { fn(source); } catch { /* a listener must not break the rest */ } });
}

function wireYtFrame(frame: HTMLIFrameElement) {
  if (!isYtFrame(frame)) return;
  if (!/[?&]enablejsapi=1/.test(frame.src)) {
    frame.src = frame.src + (frame.src.includes("?") ? "&" : "?") + "enablejsapi=1";
  }
  const hello = () => {
    try { frame.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: "bb-focus", channel: "widget" }), "*"); } catch { /* ignore */ }
  };
  if (!(frame as any).__bbFocusWired) {
    (frame as any).__bbFocusWired = true;
    frame.addEventListener("load", hello);
  }
  hello();
}

// <video>/<audio>: play and unmute events don't bubble — captured on the
// document, and listened to directly on the (detached) player element.
function onMedia(e: Event) {
  const el = e.target as HTMLMediaElement;
  if (!(el instanceof HTMLMediaElement) || el.paused || el.muted || el.volume === 0) return;
  claim(el === getPlayerAudio() ? "bb-player" : el);
}

function wirePlayer() {
  const el = getPlayerAudio();
  if (!el || wiredPlayers.has(el)) return;
  wiredPlayers.add(el);
  el.addEventListener("play", onMedia);
  el.addEventListener("volumechange", onMedia);
}

export function installMediaFocus(playerAudio: () => HTMLMediaElement | null) {
  getPlayerAudio = playerAudio;
  if (typeof window === "undefined") return;
  wirePlayer();
  if (installed) return;
  installed = true;

  document.addEventListener("play", onMedia, true);
  document.addEventListener("volumechange", onMedia, true);

  // YouTube embeds report state over postMessage once they hear "listening".
  window.addEventListener("message", (e: MessageEvent) => {
    if (!YT_ORIGINS.includes(e.origin)) return;
    let data: any;
    try { data = typeof e.data === "string" ? JSON.parse(e.data) : e.data; } catch { return; }
    if (!data || typeof data !== "object") return;
    const frame = [...document.querySelectorAll("iframe")].find((f) => (f as HTMLIFrameElement).contentWindow === e.source) as HTMLIFrameElement | undefined;
    if (!frame) return;
    const st = ytState.get(frame) || { playing: false, muted: false };
    const wasAudible = st.playing && !st.muted;
    const info = data.info;
    if (data.event === "onStateChange" && typeof info === "number") st.playing = info === 1;
    if (data.event === "infoDelivery" && info && typeof info === "object") {
      if (typeof info.playerState === "number") st.playing = info.playerState === 1;
      if (typeof info.muted === "boolean") st.muted = info.muted;
      if (typeof info.volume === "number" && info.volume === 0) st.muted = true;
    }
    ytState.set(frame, st);
    if (!wasAudible && st.playing && !st.muted) claim(frame.hasAttribute("data-bb-player-yt") ? "bb-player" : frame);
  });

  // Wire every YouTube iframe, now and as they appear or change src.
  document.querySelectorAll("iframe").forEach((f) => wireYtFrame(f as HTMLIFrameElement));
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "attributes" && r.target instanceof HTMLIFrameElement) { wireYtFrame(r.target); continue; }
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n instanceof HTMLIFrameElement) wireYtFrame(n);
        n.querySelectorAll?.("iframe").forEach((f) => wireYtFrame(f as HTMLIFrameElement));
      });
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
}

/** Called when something else takes focus — the BB player pauses itself. */
export function onMediaFocus(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
